// Chakra imports
import {
  Box,
  Text,
  useColorModeValue,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  Button,
  SimpleGrid,
  Select,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Grid,
  GridItem,
  Textarea,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "@chakra-ui/react";
import { MdArrowBack, MdLocationOn } from "react-icons/md";
import centersService, {
  PHASE_STATUSES,
} from "services/centersService";
import regionsService from "services/regionsService";

let phaseIdCounter = 0;
const nextPhaseId = () => {
  phaseIdCounter += 1;
  return `phase-${Date.now()}-${phaseIdCounter}`;
};

const createEmptyPhase = () => ({
  _id: nextPhaseId(),
  name: "",
  description: "",
  amount: "",
  due_date: "",
  status: "active",
});

export default function CenterForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isEdit = Boolean(id);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const cardBg = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.100");
  const inputBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const iconBoxBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const phaseCardBg = useColorModeValue("gray.50", "whiteAlpha.50");

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [regions, setRegions] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    full_name: "",
    region_id: "",
    phases: [createEmptyPhase()],
  });
  const [formErrors, setFormErrors] = useState({});

  const fetchRegions = async () => {
    try {
      const rRes = await regionsService.getRegions();
      if (rRes.success && Array.isArray(rRes.data)) setRegions(rRes.data);
    } catch (err) {
      console.error("Error fetching regions:", err);
    }
  };

  const fetchCenter = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await centersService.getCenters();
      if (!result.success || !result.data) {
        setError(result.error || "Failed to load center");
        return;
      }
      const center = result.data.find((c) => c.id === id);
      if (!center) {
        setError("Center not found");
        return;
      }
      const phasesFromApi = Array.isArray(center.phases)
        ? center.phases.map((phase) => ({
            _id: nextPhaseId(),
            name: phase?.name || "",
            description: phase?.description || "",
            amount: phase?.amount ?? "",
            due_date: phase?.due_date ? String(phase.due_date).slice(0, 10) : "",
            status: PHASE_STATUSES.includes(phase?.status) ? phase.status : "inactive",
          }))
        : [];

      const legacyPhaseConfig = center.phase_config || {};
      const phasesFromLegacy = Object.entries(legacyPhaseConfig).map(([name, config]) => {
        const amountMatch = (center.amounts || []).find((a) => a.phase === name);
        return {
          _id: nextPhaseId(),
          name,
          description: "",
          amount: amountMatch?.value ?? "",
          due_date: config?.due_date ? String(config.due_date).slice(0, 10) : "",
          status: PHASE_STATUSES.includes(config?.status) ? config.status : "inactive",
        };
      });

      const phases = phasesFromApi.length
        ? phasesFromApi
        : phasesFromLegacy.length
          ? phasesFromLegacy
          : [createEmptyPhase()];

      setFormData({
        name: center.name || "",
        full_name: center.full_name || "",
        region_id: center.region_id || "",
        phases,
      });
    } catch (err) {
      console.error("Error fetching center:", err);
      setError("Failed to load center.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegions();
  }, []);

  useEffect(() => {
    if (id) fetchCenter();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCenter is stable
  }, [id]);

  const buildPhasesPayload = () =>
    (formData.phases || [])
      .map((phase) => ({
        name: phase.name?.trim(),
        description: phase.description?.trim() || "",
        amount: phase.amount === "" ? 0 : Number(phase.amount),
        due_date: phase.due_date && String(phase.due_date).trim() ? String(phase.due_date).trim() : null,
        status: PHASE_STATUSES.includes(phase.status) ? phase.status : "inactive",
      }))
      .filter((phase) => phase.name);

  const handlePhaseChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      phases: prev.phases.map((phase, idx) => (idx === index ? { ...phase, [field]: value } : phase)),
    }));
  };

  const handleAddPhase = () => {
    setFormData((prev) => ({
      ...prev,
      phases: [...prev.phases, createEmptyPhase()],
    }));
  };

  const handleRemovePhase = (index) => {
    setFormData((prev) => {
      const nextPhases = prev.phases.filter((_, idx) => idx !== index);
      return {
        ...prev,
        phases: nextPhases.length ? nextPhases : [createEmptyPhase()],
      };
    });
  };

  const validate = () => {
    const errors = {};
    if (!formData.name || !formData.name.trim()) errors.name = "Name is required";
    if (!formData.full_name || !formData.full_name.trim()) errors.full_name = "Full name is required";
    if (!formData.region_id) errors.region_id = "Region is required";

    const phasesPayload = buildPhasesPayload();
    if (!phasesPayload.length) {
      errors.phases = "Add at least one phase with a name.";
    }
    if (phasesPayload.some((phase) => !Number.isFinite(phase.amount) || phase.amount < 0)) {
      errors.phases = "Each phase amount must be a number greater than or equal to 0.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        full_name: formData.full_name.trim(),
        region_id: formData.region_id,
        phases: buildPhasesPayload(),
      };
      const result = isEdit
        ? await centersService.updateCenter(id, payload)
        : await centersService.createCenter(payload);
      if (result.success) {
        toast({
          title: "Success",
          description: result.message || (isEdit ? "Center updated." : "Center created."),
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        navigate("/admin/centers");
      } else {
        toast({
          title: "Error",
          description: result.error || "Request failed",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Flex justify="center" align="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Flex>
      </Box>
    );
  }

  if (error && isEdit) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Alert status="error" borderRadius="15px" mb="4">
          <AlertIcon />
          <Text color={textColor}>{error}</Text>
        </Alert>
        <Button leftIcon={<MdArrowBack />} variant="ghost" onClick={() => navigate("/admin/centers")}>
          Back to Centers
        </Button>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} maxW="1200px" mx="auto" pb="8">
      <Flex direction="column" gap="6">
        <Flex align="center" gap="4">
          <Button
            leftIcon={<MdArrowBack />}
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/centers")}
          >
            Back
          </Button>
          <Flex align="center" gap="3">
            <Flex w="10" h="10" borderRadius="xl" bg={iconBoxBg} align="center" justify="center">
              <Icon as={MdLocationOn} color="brand.500" boxSize="5" />
            </Flex>
            <Text color={textColor} fontSize="xl" fontWeight="700">
              {isEdit ? "Edit Center" : "New Center"}
            </Text>
          </Flex>
        </Flex>

        <Card p="6" bg={cardBg} borderRadius="16px" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          <Text color={textColor} fontWeight="600" mb="4">
            Basic information
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
            <FormControl isRequired isInvalid={!!formErrors.name}>
              <FormLabel color={textColor}>Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (formErrors.name) setFormErrors({ ...formErrors, name: null });
                }}
                placeholder="e.g. ABOBOH"
                bg={inputBg}
                color={textColor}
              />
              {formErrors.name && (
                <Text color="red.500" fontSize="sm" mt="1">
                  {formErrors.name}
                </Text>
              )}
            </FormControl>
            <FormControl isRequired isInvalid={!!formErrors.full_name}>
              <FormLabel color={textColor}>Full name</FormLabel>
              <Input
                value={formData.full_name}
                onChange={(e) => {
                  setFormData({ ...formData, full_name: e.target.value });
                  if (formErrors.full_name) setFormErrors({ ...formErrors, full_name: null });
                }}
                placeholder="e.g. ABOBOH BUILDING"
                bg={inputBg}
                color={textColor}
              />
              {formErrors.full_name && (
                <Text color="red.500" fontSize="sm" mt="1">
                  {formErrors.full_name}
                </Text>
              )}
            </FormControl>
            <FormControl isRequired isInvalid={!!formErrors.region_id} gridColumn={{ base: "1", md: "1 / -1" }}>
              <FormLabel color={textColor}>Region</FormLabel>
              <Select
                value={formData.region_id}
                onChange={(e) => {
                  setFormData({ ...formData, region_id: e.target.value });
                  if (formErrors.region_id) setFormErrors({ ...formErrors, region_id: null });
                }}
                placeholder="Select region"
                bg={inputBg}
                color={textColor}
                maxW="400px"
              >
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                    {r.code ? ` (${r.code})` : ""}
                  </option>
                ))}
              </Select>
              {formErrors.region_id && (
                <Text color="red.500" fontSize="sm" mt="1">
                  {formErrors.region_id}
                </Text>
              )}
            </FormControl>
          </SimpleGrid>
        </Card>

        <Card p="6" bg={cardBg} borderRadius="16px" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          <Flex justify="space-between" align={{ base: "start", md: "center" }} mb="4" gap="3" flexWrap="wrap">
            <Box>
              <Text color={textColor} fontWeight="600">
                Tuition phases
              </Text>
              <Text color={textColorSecondary} fontSize="sm" mt="1">
                Add and manage custom phases for this center. Only active phases will be available for tuition operations.
              </Text>
            </Box>
            <Button size="sm" variant="outline" colorScheme="brand" onClick={handleAddPhase}>
              Add Phase
            </Button>
          </Flex>
          {formErrors.phases && (
            <Text color="red.500" fontSize="sm" mb="4">
              {formErrors.phases}
            </Text>
          )}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="5">
            {(formData.phases || []).map((phase, index) => (
              <Box
                key={phase._id || `phase-${index}`}
                p="4"
                borderRadius="lg"
                borderWidth="1px"
                borderColor={borderColor}
                bg={phaseCardBg}
              >
                <Flex justify="space-between" align="center" mb="3" gap="3">
                  <Text color={textColor} fontWeight="600" fontSize="sm">
                    Phase {index + 1}
                  </Text>
                  <Button
                    size="xs"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => handleRemovePhase(index)}
                  >
                    Remove
                  </Button>
                </Flex>
                <Grid templateColumns="1fr 1fr" gap="3">
                  <GridItem colSpan={{ base: 2, sm: 1 }}>
                    <FormControl isRequired>
                      <FormLabel color={textColor} fontSize="xs">
                        Name
                      </FormLabel>
                      <Input
                        size="sm"
                        value={phase.name}
                        onChange={(e) => handlePhaseChange(index, "name", e.target.value)}
                        placeholder="e.g. First Installment"
                        bg={inputBg}
                        color={textColor}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={{ base: 2, sm: 1 }}>
                    <FormControl>
                      <FormLabel color={textColor} fontSize="xs">
                        Amount
                      </FormLabel>
                      <Input
                        size="sm"
                        type="number"
                        min="0"
                        value={phase.amount}
                        onChange={(e) => handlePhaseChange(index, "amount", e.target.value)}
                        placeholder="e.g. 50000"
                        bg={inputBg}
                        color={textColor}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={2}>
                    <FormControl>
                      <FormLabel color={textColor} fontSize="xs">
                        Description
                      </FormLabel>
                      <Textarea
                        size="sm"
                        value={phase.description}
                        onChange={(e) => handlePhaseChange(index, "description", e.target.value)}
                        placeholder="Short description for this tuition phase"
                        bg={inputBg}
                        color={textColor}
                        rows={3}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={{ base: 2, sm: 1 }}>
                    <FormControl>
                      <FormLabel color={textColor} fontSize="xs">
                        Due date
                      </FormLabel>
                      <Input
                        type="date"
                        size="sm"
                        value={phase.due_date || ""}
                        onChange={(e) => handlePhaseChange(index, "due_date", e.target.value)}
                        bg={inputBg}
                        color={textColor}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={{ base: 2, sm: 1 }}>
                    <FormControl>
                        <FormLabel color={textColor} fontSize="xs">
                          Status
                        </FormLabel>
                        <Select
                          value={phase.status || "inactive"}
                          onChange={(e) => handlePhaseChange(index, "status", e.target.value)}
                          size="sm"
                          bg={inputBg}
                          color={textColor}
                        >
                          {PHASE_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                  </GridItem>
                </Grid>
              </Box>
            ))}
          </SimpleGrid>
        </Card>

        <Flex gap="3" justify="flex-start">
          <Button
            colorScheme="brand"
            onClick={handleSubmit}
            isLoading={saving}
            loadingText={isEdit ? "Updating" : "Creating"}
          >
            {isEdit ? "Update Center" : "Create Center"}
          </Button>
          <Button variant="outline" onClick={() => navigate("/admin/centers")} isDisabled={saving}>
            Cancel
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}
